import React from 'react';
import { formatCurrency } from 'apps/sigma2-ui/src/lib/number-formatting';
import { Badge } from 'apps/sigma2-ui/src/components/ui/Badge';
import { FlatCard } from 'apps/sigma2-ui/src/components/ui/FlatCard';
import { KeyValuePair } from 'apps/sigma2-ui/src/components/ui/KeyValuePair';
import { Value } from 'apps/sigma2-ui/src/components/ui/KeyValuePair';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { isPercentageNegligible } from 'apps/sigma2-ui/src/components/Percentage';
import { isPercentageTooHuge } from 'apps/sigma2-ui/src/components/Percentage';
import { Percentage } from 'apps/sigma2-ui/src/components/Percentage';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { GetPrivilegedLevelForDimensionFn } from 'libs/end-to-end/src/dimension';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

interface PayCorrectionAnalysisCardProps {
  heading: React.ReactNode;
  value: React.ReactNode;
  footer: React.ReactNode;
  difference: React.ReactNode;
}
/**
 * This just has styling and spacing for the cards to simplify refactoring the styles and spacing.
 */
const PayCorrectionAnalysisCard: React.FC<PayCorrectionAnalysisCardProps> = ({
  heading,
  value,
  footer,
  difference,
}) => {
  return (
    <FlatCard className="bg-white w-full">
      <Spacing direction="horizontal" size="sm" className="justify-between">
        <Spacing direction="vertical" size="xs" className="p-2 w-full">
          <KeyValuePair title={<div className="text-sm w-full">{heading}</div>}>
            <Spacing
              direction="horizontal"
              size="sm"
              className="justify-between w-full"
            >
              <Value variant="md">{value}</Value>

              <div>{difference}</div>
            </Spacing>
          </KeyValuePair>
          <Paragraph className="text-xs">{footer}</Paragraph>
        </Spacing>
      </Spacing>
    </FlatCard>
  );
};

interface PayCorrectionAnalysisCardsProps {
  budget: { needed: number; recommended: number };
  genderPayGap: { projected: number; current: number };
  racialPayGap: { projected: number; current: number };
  language: Phrases;
  getPrivilegedLevelForDimension: GetPrivilegedLevelForDimensionFn;
}

export const PayCorrectionAnalysisCards: React.FC<
  PayCorrectionAnalysisCardsProps
> = ({
  budget,
  racialPayGap,
  genderPayGap,
  language,
  getPrivilegedLevelForDimension,
}) => {
  return (
    <Spacing
      direction="horizontal"
      size="xl"
      className="px-8 justify-between mb-4"
    >
      <PayCorrectionAnalysisCard
        heading="Budget Needed"
        value={formatCurrency(budget.needed)}
        footer={`Recommended: ${formatCurrency(budget.recommended)}`}
        difference={
          <BadgedPercentDifference
            before={budget.recommended}
            after={budget.needed}
          />
        }
      />

      <PayCorrectionAnalysisCard
        heading={
          <PayGapHeading
            title={'Projected Gender Pay Gap'}
            tooltipContents={language.payCorrectionProjectedGenderPayGapTooltip(
              { getPrivilegedLevelForDimension },
            )}
            tooltipLinkLabel={'Projected Gender Pay Gap Information Icon'}
          />
        }
        value={<Percentage percentage={genderPayGap.projected} />}
        footer={
          <>
            Current: <Percentage percentage={genderPayGap.current} />
          </>
        }
        difference={
          <BadgedPercentDifference
            before={genderPayGap.current}
            after={genderPayGap.projected}
          />
        }
      />

      <PayCorrectionAnalysisCard
        heading={
          <PayGapHeading
            title={'Projected Racial Pay Gap'}
            tooltipContents={language.payCorrectionProjectedRacePayGapTooltip({
              getPrivilegedLevelForDimension,
            })}
            tooltipLinkLabel={'Projected Racial Pay Gap Information Icon'}
          />
        }
        value={<Percentage percentage={racialPayGap.projected} />}
        footer={
          <>
            Current: <Percentage percentage={racialPayGap.current} />
          </>
        }
        difference={
          <BadgedPercentDifference
            before={racialPayGap.current}
            after={racialPayGap.projected}
          />
        }
      />
    </Spacing>
  );
};

const PayGapTooltip = ({
  contents,
  label,
}: {
  contents: string;
  label: string;
}) => {
  const language = useLanguage();

  return (
    <LightTooltip
      placement="right-start"
      title={
        <S2Typography
          variant={'small-label'}
          translatedContent={language.payGapTooltipContents({ contents })}
        />
      }
    >
      <span aria-label={label}>
        <IconInformationSvg svg={{ className: 'w-4 h-4' }} />
      </span>
    </LightTooltip>
  );
};

const PayGapHeading = ({
  title,
  tooltipContents,
  tooltipLinkLabel,
}: {
  title: string;
  tooltipContents: string;
  tooltipLinkLabel: string;
}) => {
  return (
    <Spacing direction={'horizontal'} size={'sm'} className={'items-center'}>
      {title}{' '}
      <PayGapTooltip contents={tooltipContents} label={tooltipLinkLabel} />
    </Spacing>
  );
};

const calcDiff = ({ before, after }: { before: number; after: number }) => {
  if (before === 0) {
    return 0;
  }

  const difference = after - before;
  return (difference / before) * 100;
};

const BadgedPercentDifference: React.FC<{
  before: number;
  after: number;
}> = ({ after, before }) => {
  const percentage = calcDiff({ before, after });

  if (isPercentageNegligible(percentage) || isPercentageTooHuge(percentage)) {
    return null;
  }

  return (
    <Badge color={'black'}>
      <Percentage percentage={percentage} />
    </Badge>
  );
};
